import { lazy, Suspense } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import ModernBrowserInterstitial from 'app/components/interstitials/ModernBrowserInterstitial';
import CompanyTypeRestrictor from 'app/components/providers/CompanyTypeRestrictor';
import AuthRoute from 'app/components/shared/AuthRoute';
import { factorRoutes, routes } from 'app/constants/routes';
import { CompanyTypeId } from 'app/constants/user';
import LoadingScreen from 'app/pages/shared/LoadingScreen';
import { retry } from 'app/utils/lib/file';

import FactorRoutesLayout from '../../Layouts/FactorRoutesLayout';

// Lazy-loaded page components
const LazyFourOhFourRoute = lazy(() => retry(() => import('app/pages/error/FourOhFour')));
const LazyLoadsRoute = lazy(() => retry(() => import('app/pages/factor/Loads')));
const LazyLoadsV2Route = lazy(() => retry(() => import('app/pages/factor/LoadsV2')));
const LazyInvoiceRoute = lazy(() => retry(() => import('app/pages/factor/Invoice')));
const LazyInvoicesRoute = lazy(() => retry(() => import('app/pages/factor/Invoices')));
const LazyInvoicesV2Route = lazy(() => retry(() => import('app/pages/factor/InvoicesV2')));
const LazyPaymentRoute = lazy(() => retry(() => import('app/pages/factor/Payment')));
const LazyLoadRoute = lazy(() => retry(() => import('app/pages/factor/Load')));
const LazyPaymentsRoute = lazy(() => retry(() => import('app/pages/factor/Payments')));
const LazyPayorsRoute = lazy(() => retry(() => import('app/pages/factor/Payors')));
const LazyProfileRoute = lazy(() => retry(() => import('app/pages/factor/Profile')));
const LazyRelationshipsRoute = lazy(() => retry(() => import('app/pages/factor/Relationships')));
const LazyRelationshipRoute = lazy(() => retry(() => import('app/pages/factor/Relationship')));
const LazyUserCreateRoute = lazy(() => retry(() => import('app/pages/factor/UserCreate')));
const LazyUserEditRoute = lazy(() => retry(() => import('app/pages/factor/UserEdit')));

// Export components with clean names
export const FourOhFour = (props: Record<string, unknown>) => <LazyFourOhFourRoute {...props} />;
export const Loads = (props: Record<string, unknown>) => <LazyLoadsRoute {...props} />;
export const LoadsV2 = (props: Record<string, unknown>) => <LazyLoadsV2Route {...props} />;
export const Invoice = (props: Record<string, unknown>) => <LazyInvoiceRoute {...props} />;
export const Invoices = (props: Record<string, unknown>) => <LazyInvoicesRoute {...props} />;
export const InvoicesV2 = (props: Record<string, unknown>) => <LazyInvoicesV2Route {...props} />;
export const Payment = (props: Record<string, unknown>) => <LazyPaymentRoute {...props} />;
export const Load = (props: Record<string, unknown>) => <LazyLoadRoute {...props} />;
export const Payments = (props: Record<string, unknown>) => <LazyPaymentsRoute {...props} />;
export const Payors = (props: Record<string, unknown>) => <LazyPayorsRoute {...props} />;
export const Profile = (props: Record<string, unknown>) => <LazyProfileRoute {...props} />;
export const Relationships = (props: Record<string, unknown>) => <LazyRelationshipsRoute {...props} />;
export const Relationship = (props: Record<string, unknown>) => <LazyRelationshipRoute {...props} />;
export const UserCreate = (props: Record<string, unknown>) => <LazyUserCreateRoute {...props} />;
export const UserEdit = (props: Record<string, unknown>) => <LazyUserEditRoute {...props} />;

// Layout component
export const FactorLayout = () => (
  <AuthRoute>
    <CompanyTypeRestrictor permittedCompanyTypeIds={[CompanyTypeId.Factor]}>
      <FactorRoutesLayout
        body={(
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        )}
      />
      <ModernBrowserInterstitial />
    </CompanyTypeRestrictor>
  </AuthRoute>
);

// Default redirect component
export const DefaultRedirect = () => {
  const defaultPath = `/${routes.factor}/${factorRoutes.payors}`;

  return <Navigate to={defaultPath} />;
};
